import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { unparse } from "papaparse";
import pluralize from "pluralize";
import { SubmissionError, reduxForm } from "redux-form";
import shortid from "shortid";
import CreateAnnotationsPage from "./CreateAnnotationsPage";
import { formName } from "./constants";
import { AutoAnnotateBpMatchingDialog } from "./AutoAnnotateBpMatchingDialog";
import {
  parseCsvFile,
  validateCSVRequiredHeaders,
  validateCSVRow
} from "@teselagen/file-utils";
import downloadjs from "downloadjs";
import {
  autoAnnotate,
  convertApELikeRegexToRegex,
  convertProteinSeqToDNAIupac,
  getFeatureToColorMap,
  getFeatureTypes
} from "@teselagen/sequence-utils";
import { showDialog } from "./GlobalDialogUtils";
import { compose } from "redux";
import {
  DataTable,
  DialogFooter,
  FileUploadField,
  InfoHelper,
  showConfirmationDialog,
  wrapDialog,
  TgSuggest
} from "@teselagen/ui";
import { startCase, some } from "lodash-es";
import withEditorProps from "./withEditorProps";
import { Button, Colors, Slider, Tab, Tabs } from "@blueprintjs/core";
import { typeField } from "./helperComponents/PropertiesDialog/typeField";
import axios from "axios";
export const FolderPicker = ({ value, onChange, autoFocus }) => {
  const [folders, setFolders] = useState([]);
  const [isEditing, setIsEditing] = useState(autoFocus);
  useEffect(() => {
    const getFolders = (node, path = "") => {
      let result = [];
      if (node.type === "directory") {
        if (path) result.push(path);
        (node.children || []).forEach((child) => {
          result = result.concat(
            getFolders(
              child,
              path ? `${path}/${child.name}` : child.name
            )
          );
        });
      }
      return result;
    };
    axios.get("/api/files").then((res) => {
      const data = res.data.files || res.data;
      const allFolders = ["."].concat(getFolders(data));
      setFolders(allFolders);
    }).catch((e) => console.error("Error fetching folders", e));
  }, []);
  if (!isEditing) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => setIsEditing(true),
        className: "folder-picker-button",
        style: {
          background: "none",
          border: "none",
          padding: "4px 8px",
          cursor: "pointer",
          borderRadius: 4,
          borderBottom: "1px dashed #666"
        },
        children: value || "."
      }
    );
  }
  return /* @__PURE__ */ jsx(
    TgSuggest,
    {
      isSimpleSearch: true,
      autoFocus: true,
      onItemSelect: (val) => {
        onChange(val);
        setIsEditing(false);
      },
      onChange,
      value,
      options: folders
    }
  );
};
export const FilePicker = ({ value, onChange, autoFocus }) => {
  const [files, setFiles] = useState([]);
  const [isEditing, setIsEditing] = useState(autoFocus);
  useEffect(() => {
    const getFiles = (node) => {
      let result = [];
      if (node.type === "file") {
        if (node.name.match(/\.(gb|gbk|genbank|gen\.gb)$/i)) {
          result.push(node.path);
        }
      } else if (node.type === "directory") {
        (node.children || []).forEach((child) => {
          result = result.concat(getFiles(child));
        });
      }
      return result;
    };
    axios.get("/api/files").then((res) => {
      const data = res.data.files || res.data;
      const allFiles = getFiles(data);
      setFiles(allFiles);
    }).catch((e) => console.error("Error fetching files", e));
  }, []);
  if (!isEditing) {
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => setIsEditing(true),
        className: "file-picker-button",
        style: {
          background: "none",
          border: "none",
          padding: "4px 8px",
          cursor: "pointer",
          borderRadius: 4,
          borderBottom: "1px dashed #666"
        },
        children: value || "choose file"
      }
    );
  }
  return /* @__PURE__ */ jsx(
    TgSuggest,
    {
      isSimpleSearch: true,
      autoFocus: true,
      onItemSelect: (val) => {
        onChange(val);
        setIsEditing(false);
      },
      onChange,
      value,
      options: files
    }
  );
};
export function autoAnnotateFeatures() {
  showDialog({
    ModalComponent: AutoAnnotateModal,
    //we want to use a ModalComponent here so our addon does not
    props: {
      annotationType: "feature"
    }
  });
}
export function autoAnnotateParts() {
  showDialog({
    ModalComponent: AutoAnnotateModal,
    //we want to use a ModalComponent here so our addon does not
    props: {
      annotationType: "part"
    }
  });
}
export function autoAnnotatePrimers() {
  showDialog({
    ModalComponent: AutoAnnotateModal,
    //we want to use a ModalComponent here so our addon does not
    props: {
      annotationType: "primer"
    }
  });
}
export const AutoAnnotateModal = compose(
  wrapDialog((p) => ({
    canEscapeKeyClose: false,
    title: `Auto Annotate ${startCase(pluralize(p.annotationType))}`
  })),
  withEditorProps,
  reduxForm({ form: formName })
)((props) => {
  const {
    sequenceData,
    handleSubmit,
    annotationType = "feature",
    error,
    getCustomAutoAnnotateList,
    hideModal
  } = props;
  console.log("[DEBUG] AutoAnnotateModal rendering, annotationType:", annotationType);
  const [fileType, setSelectedImportType] = useState("fromFolder");
  const [selectedFolderPath, setSelectedFolderPath] = useState(() => {
    let defFolder = ".";
    let filePath = "";
    if (sequenceData?.id && sequenceData.id.includes("/")) {
      filePath = sequenceData.id;
    } else {
      const hash = window.location.hash;
      if (hash.includes("file=")) {
        const params = new URLSearchParams(hash.split("?")[1]);
        filePath = params.get("file");
      }
    }
    if (filePath) {
      const parts = filePath.split("/");
      parts.pop();
      defFolder = parts.join("/");
    }
    return defFolder;
  });
  const storageKey = `lastAutoAnnotateFile_${sequenceData?.id || "default"}`;
  const [selectedFilePath, setSelectedFilePath] = useState(
    () => localStorage.getItem(storageKey) || ""
  );
  const handleFilePathChange = (val) => {
    setSelectedFilePath(val);
    localStorage.setItem(storageKey, val);
  };
  const [similarity, setSimilarity] = useState(100);
  const [minLength, setMinLength] = useState(9);
  const [folderFeaturesLoading, setFolderFeaturesLoading] = useState(false);
  const [newAnnotations, setNewAnns] = useState(false);
  const [customAnnResponse, setCustomAnnResponse] = useState();
  const [loadingCustomAnnList, setLoadingCustomAnnList] = useState();
  useEffect(() => {
    (async () => {
      if (getCustomAutoAnnotateList) {
        setLoadingCustomAnnList(true);
        try {
          const anns = await getCustomAutoAnnotateList(props);
          setCustomAnnResponse(anns);
        } catch (e) {
          window.toastr.warning("Error loading custom annotation list");
          console.error(`e:`, e);
        } finally {
          setLoadingCustomAnnList(false);
        }
      }
    })();
  }, []);
  if (newAnnotations) {
    return /* @__PURE__ */ jsx(
      CreateAnnotationsPage,
      {
        ...props,
        setNewAnns,
        newAnnotations
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: "bp3-dialog-body", children: [
    /* @__PURE__ */ jsxs(
      Tabs,
      {
        renderActiveTabPanelOnly: true,
        onChange: setSelectedImportType,
        selectedTabId: fileType,
        children: [
          /* @__PURE__ */ jsx(
            Tab,
            {
              panel: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsxs("div", { children: [
                  "Select a CSV file (",
                  /* @__PURE__ */ jsx(
                    "a",
                    {
                      onClick: () => {
                        const rows = [
                          {
                            name: `Example ${startCase(annotationType)} 1`,
                            description: "I'm a description",
                            sequence: `gatNNtacaggttt`,
                            ...annotationType === "feature" && {
                              type: `cds`
                            },
                            isRegex: false,
                            matchType: "dna"
                          },
                          {
                            name: `Example Protein ${startCase(annotationType)}`,
                            description: "I'm a description",
                            sequence: `APGSGTGGGSGSAPG`,
                            ...annotationType === "feature" && {
                              type: `cds`
                            },
                            isRegex: false,
                            matchType: "protein"
                          },
                          {
                            name: `Example ${startCase(annotationType)} 2`,
                            description: "I'm another description",
                            sequence: `gat.*tacccc.*aggttt`,
                            ...annotationType === "feature" && {
                              type: `cds`
                            },
                            isRegex: true,
                            matchType: "dna"
                          }
                        ];
                        const csv = unparse(rows);
                        downloadjs(
                          csv,
                          `Example CSV Annotation Upload File.csv`,
                          "text/plain"
                        );
                      },
                      children: "download example"
                    }
                  ),
                  ") with the following columns:",
                  /* @__PURE__ */ jsx("br", {}),
                  /* @__PURE__ */ jsx("br", {}),
                  /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
                    "name,description,sequence,type,",
                    /* @__PURE__ */ jsxs("span", { style: { display: "flex" }, children: [
                      "isRegex \xA0",
                      /* @__PURE__ */ jsx(
                        InfoHelper,
                        {
                          onClick: (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            showDialog({
                              ModalComponent: AutoAnnotateBpMatchingDialog
                            });
                          },
                          content: /* @__PURE__ */ jsx("span", { children: "Any valid regexes allowed. Click for more info about regex matching" })
                        }
                      )
                    ] }),
                    ",matchType"
                  ] }),
                  /* @__PURE__ */ jsx("br", {}),
                  annotationType !== "feature" && /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx("i", { children: 'Note: the "type" column is optional' }),
                    /* @__PURE__ */ jsx("br", {})
                  ] })
                ] }),
                /* @__PURE__ */ jsx(
                  FileUploadField,
                  {
                    validateAgainstSchema,
                    name: "csvFile",
                    fileLimit: 1,
                    isRequired: true,
                    accept: ".csv"
                  }
                )
              ] }),
              id: "csvFile",
              title: "CSV"
            }
          ),
          /* @__PURE__ */ jsx(
            Tab,
            {
              panel: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsxs("div", { children: [
                  "Select an ApE style features .txt file (",
                  /* @__PURE__ */ jsx(
                    "a",
                    {
                      onClick: () => {
                        downloadjs(
                          `T3	ATTAACCCTCACTAAAGGGA	primer_bind	cyan	green	0	0
M13-fwd	TGTAAAACGACGGCCAGT	primer_bind	cyan	green	0	0
FRT	GAAGTTCCTATTCTCTAGAAAGTATAGGAACTTC	misc_recomb	orchid	pink	0	0`,
                          `Example APE Feature List Upload File.txt`,
                          "text/plain"
                        );
                      },
                      children: "download example"
                    }
                  ),
                  "):"
                ] }),
                /* @__PURE__ */ jsx(
                  FileUploadField,
                  {
                    fileLimit: 1,
                    name: "apeFile",
                    isRequired: true,
                    accept: ".txt"
                  }
                ),
                error && /* @__PURE__ */ jsx("div", { style: { padding: 5, color: Colors.RED1 }, children: error })
              ] }),
              id: "apeFile",
              title: "ApE File"
            }
          ),
          /* @__PURE__ */ jsx(
            Tab,
            {
              panel: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("div", { style: { marginBottom: 10 }, children: "Select a folder containing sequences to extract features from:" }),
                /* @__PURE__ */ jsx(
                  FolderPicker,
                  {
                    value: selectedFolderPath,
                    onChange: setSelectedFolderPath
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 20,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      paddingRight: 25
                    },
                    children: [
                      /* @__PURE__ */ jsx("span", { style: { marginRight: 20, flexShrink: 0, width: 105 }, children: "Min Length (bp):" }),
                      /* @__PURE__ */ jsx(
                        "input",
                        {
                          type: "number",
                          min: 1,
                          value: minLength,
                          onChange: (e) => setMinLength(e.target.value),
                          style: { width: 60 }
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 10,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      paddingRight: 25
                    },
                    children: [
                      /* @__PURE__ */ jsxs("span", { style: { marginRight: 20, flexShrink: 0, width: 105 }, children: [
                        "Similarity: ",
                        similarity,
                        "%"
                      ] }),
                      /* @__PURE__ */ jsx(
                        Slider,
                        {
                          min: 50,
                          max: 100,
                          stepSize: 1,
                          labelStepSize: 5,
                          value: similarity,
                          onChange: setSimilarity,
                          labelRenderer: (val) => /* @__PURE__ */ jsxs("span", { style: { fontSize: 10 }, children: [
                            val,
                            "%"
                          ] })
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      display: "flex",
                      justifyContent: "space-between",
                      paddingLeft: 125,
                      paddingRight: 25,
                      marginBottom: 10
                    },
                    children: [85, 90, 95, 100].map((val) => /* @__PURE__ */ jsx(
                      Button,
                      {
                        minimal: true,
                        small: true,
                        onClick: () => setSimilarity(val),
                        text: `${val}%`,
                        style: { fontSize: 10, height: 20, minHeight: 20 }
                      },
                      val
                    ))
                  }
                ),
                error && /* @__PURE__ */ jsx("div", { style: { padding: 5, color: Colors.RED1 }, children: error })
              ] }),
              id: "fromFolder",
              title: "From Folder"
            }
          ),
          /* @__PURE__ */ jsx(
            Tab,
            {
              panel: /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("div", { style: { marginBottom: 10 }, children: "Select a GenBank file to extract features from:" }),
                /* @__PURE__ */ jsx(
                  FilePicker,
                  {
                    value: selectedFilePath,
                    onChange: handleFilePathChange
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 20,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      paddingRight: 25
                    },
                    children: [
                      /* @__PURE__ */ jsx("span", { style: { marginRight: 20, flexShrink: 0, width: 105 }, children: "Min Length (bp):" }),
                      /* @__PURE__ */ jsx(
                        "input",
                        {
                          type: "number",
                          min: 1,
                          value: minLength,
                          onChange: (e) => setMinLength(e.target.value),
                          style: { width: 60 }
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      marginTop: 10,
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      paddingRight: 25
                    },
                    children: [
                      /* @__PURE__ */ jsxs("span", { style: { marginRight: 20, flexShrink: 0, width: 105 }, children: [
                        "Similarity: ",
                        similarity,
                        "%"
                      ] }),
                      /* @__PURE__ */ jsx(
                        Slider,
                        {
                          min: 50,
                          max: 100,
                          stepSize: 1,
                          labelStepSize: 5,
                          value: similarity,
                          onChange: setSimilarity,
                          labelRenderer: (val) => /* @__PURE__ */ jsxs("span", { style: { fontSize: 10 }, children: [
                            val,
                            "%"
                          ] })
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      display: "flex",
                      justifyContent: "space-between",
                      paddingLeft: 125,
                      paddingRight: 25,
                      marginBottom: 10
                    },
                    children: [85, 90, 95, 100].map((val) => /* @__PURE__ */ jsx(
                      Button,
                      {
                        minimal: true,
                        small: true,
                        onClick: () => setSimilarity(val),
                        text: `${val}%`,
                        style: { fontSize: 10, height: 20, minHeight: 20 }
                      },
                      val
                    ))
                  }
                ),
                error && /* @__PURE__ */ jsx("div", { style: { padding: 5, color: Colors.RED1 }, children: error })
              ] }),
              id: "fromFile",
              title: "From File"
            }
          ),
          getCustomAutoAnnotateList && (loadingCustomAnnList ? /* @__PURE__ */ jsx(Tab, { disabled: true, title: "Loading..." }) : customAnnResponse && customAnnResponse.list && /* @__PURE__ */ jsx(
            Tab,
            {
              id: "implementerDefined",
              title: customAnnResponse.title || "Custom List",
              panel: customAnnResponse.list.length ? /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                DataTable,
                {
                  isInfinite: true,
                  formName: "customAnnsTable",
                  schema: annotationType === "feature" ? customAnnsSchema : customAnnsSchemaNoType,
                  entities: customAnnResponse.list
                }
              ) }) : /* @__PURE__ */ jsx("div", { children: "No Annotations Found" })
            }
          ))
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      DialogFooter,
      {
        containerClassname: "auto-annotate-footer",
        hideModal,
        onClick: handleSubmit(async ({ apeFile, csvFile }) => {
          let convertNonStandardTypes = false;
          const annsToCheck = [];
          try {
            const validateRow = async (row, rowName) => {
              const { type = "", sequence } = row;
              let regexConvertedSeq;
              if (annotationType === "feature") {
                const cleanedType = getFeatureTypes().find(
                  (t) => t.toLowerCase() === type.toLowerCase()
                );
                if (!cleanedType) {
                  if (!convertNonStandardTypes) {
                    convertNonStandardTypes = await showConfirmationDialog({
                      cancelButtonText: "Stop Auto-Annotate",
                      text: `Detected that ${rowName} has a non-standard type of ${type}. We will assign it and all subsequent non-standard types to use the misc_feature type instead`
                    });
                    if (!convertNonStandardTypes) {
                      throw {
                        validationError: `${rowName} specifies the feature type ${type} which is not valid`
                      };
                    }
                  }
                  row.type = "misc_feature";
                } else {
                  row.type = cleanedType;
                }
              }
              if (!sequence) {
                throw {
                  validationError: `${rowName} did not have a sequence`
                };
              }
              if (row.isRegex && row.isRegex.toUpperCase() === "TRUE") {
                try {
                  new RegExp(regexConvertedSeq);
                } catch (error2) {
                  throw {
                    validationError: `${rowName} has an invalid sequence/regex. Please fix it manually.`
                  };
                }
                row.isRegex = true;
              } else {
                row.isRegex = void 0;
              }
              annsToCheck.push(row);
            };
            if (fileType === "fromFolder" || fileType === "fromFile") {
              setFolderFeaturesLoading(true);
              try {
                const endpoint = fileType === "fromFolder" ? `/api/folder/features?path=${encodeURIComponent(
                  selectedFolderPath
                )}` : `/api/file/features?path=${encodeURIComponent(
                  selectedFilePath
                )}`;
                const res = await axios.get(endpoint);
                const features = res.data.features || [];
                const parsedMinLength = parseInt(minLength, 10) || 0;
                for (const [i, feat] of features.entries()) {
                  if (!feat.sequence || feat.sequence.length < parsedMinLength)
                    continue;
                  await validateRow(
                    {
                      name: feat.name,
                      sequence: feat.sequence,
                      matchType: "dna",
                      type: feat.type || "misc_feature",
                      isRegex: void 0,
                      fileSource: feat.fileSource
                    },
                    `${fileType === "fromFolder" ? "Folder" : "File"} Feature ${i + 1} (${feat.name})`
                  );
                }
              } finally {
                setFolderFeaturesLoading(false);
              }
            } else if (fileType === "implementerDefined") {
              for (const [
                // eslint-disable-next-line no-unused-vars
                i,
                // eslint-disable-next-line no-unused-vars
                { name, sequence, matchType, type, isRegex, fileSource }
              ] of customAnnResponse.list.entries()) {
                await validateRow(
                  {
                    name,
                    sequence,
                    matchType,
                    type,
                    isRegex: isRegex ? "TRUE" : "FALSE",
                    fileSource
                  },
                  `Row ${i + 1} (${name})`
                );
              }
            } else if (fileType === "csvFile") {
              const csvHeaders = ["name", "description", "sequence"];
              if (annotationType === "feature") {
                csvHeaders.push("type");
              }
              csvHeaders.push("isRegex");
              const {
                data,
                meta: { fields }
              } = await parseCsvFile(csvFile[0]);
              const error2 = validateCSVRequiredHeaders(fields, csvHeaders);
              if (error2) {
                throw {
                  validationError: error2
                };
              }
              for (const [index, row] of data.entries()) {
                const error3 = validateCSVRow(row, csvHeaders, index);
                if (error3) {
                  throw {
                    validationError: error3
                  };
                }
                await validateRow({
                  fileSource: csvFile[0].name,
                  ...row
                }, `Row ${index + 1} (${row.name})`);
              }
            } else if (fileType === "apeFile") {
              const { data } = await parseCsvFile(apeFile[0], {
                header: false
              });
              for (const [i, [name, sequence, type]] of data.entries()) {
                await validateRow(
                  { name, sequence, type, fileSource: apeFile[0].name },
                  `Row ${i + 1} (${name})`
                );
              }
            } else {
              console.info(`we shouldn't be here!`);
              console.info(`fileType:`, fileType);
            }
            if (!annsToCheck.length) {
              return window.toastr.warning(
                "No Annotations Detected on File. Please check that your file is in the correct format."
              );
            }
            const annotationsToCheckById = {};
            annsToCheck.forEach((ann) => {
              if (ann.matchType === "protein") {
                ann.sequence = convertProteinSeqToDNAIupac(ann.sequence);
              }
              const id = shortid();
              annotationsToCheckById[id] = {
                ...ann,
                sequence: ann.isRegex ? ann.sequence : convertApELikeRegexToRegex(ann.sequence),
                id
              };
            });
            const seqId = "placeholderId";
            const { [seqId]: newAnns } = autoAnnotate({
              seqsToAnnotateById: {
                [seqId]: { ...sequenceData, id: seqId }
              },
              annotationsToCheckById,
              similarity
            });
            if (newAnns && newAnns.length) {
              const existingAnns = sequenceData[pluralize(annotationType)] || {};
              const filteredNewAnns = newAnns.filter((a) => {
                const match = annotationsToCheckById[a.id];
                const name = (match.name || "").toLowerCase();
                return !some(existingAnns, (existing) => {
                  return existing.name?.toLowerCase() === name && existing.start === a.start && existing.end === a.end && (existing.strand || (existing.forward ? 1 : -1)) === a.strand;
                });
              });
              if (filteredNewAnns.length) {
                setNewAnns(
                  filteredNewAnns.map((a) => {
                    const toRet = {
                      ...annotationsToCheckById[a.id],
                      ...a,
                      forward: a.strand !== -1,
                      id: shortid()
                    };
                    toRet.color = toRet.color || getFeatureToColorMap()[toRet.type];
                    return toRet;
                  })
                );
              } else {
                window.toastr.warning(
                  `All detected ${annotationType}s are already present on the sequence.`
                );
              }
            } else {
              window.toastr.warning(
                `No ${annotationType}s detected on sequence.`
              );
            }
          } catch (error2) {
            console.error(`error:`, error2);
            if (error2.validationError) {
              throw new SubmissionError({ [fileType]: error2.validationError });
            } else {
              window.toastr.error(
                `Error annotating ${annotationType}(s). Double check your file to make sure it is valid!`
              );
            }
          }
        }),
        text: folderFeaturesLoading ? "Loading..." : "Search",
        disabled: folderFeaturesLoading || fileType === "implementerDefined" && !(customAnnResponse && customAnnResponse.list && customAnnResponse.list.length)
      }
    )
  ] });
});
if (!window._ove_addons) window._ove_addons = {};
window._ove_addons.autoAnnotateFeatures = autoAnnotateFeatures;
window._ove_addons.autoAnnotateParts = autoAnnotateParts;
window._ove_addons.autoAnnotatePrimers = autoAnnotatePrimers;
const customAnnsSchema = ["name", "sequence", typeField, "isRegex"];
const customAnnsSchemaNoType = ["name", "sequence", typeField, "isRegex"];
const validateAgainstSchema = {
  fields: [
    {
      path: "name",
      type: "string",
      isRequired: true
    },
    {
      path: "description",
      type: "string"
    },
    {
      path: "sequence",
      type: "string",
      isRequired: true
    },
    {
      path: "type",
      type: "dropdown",
      values: getFeatureTypes(),
      defaultValue: "misc_feature"
    },
    {
      path: "isRegex",
      type: "boolean"
    },
    {
      path: "fileSource",
      type: "string",
      displayName: "File Source"
    },
    {
      path: "matchType",
      type: "dropdown",
      defaultValue: "dna",
      values: ["dna", "protein"]
    }
  ]
};
